import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@shared/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@shared/ui/radio-group';
import { Label } from '@shared/ui/label';
import { Input } from '@shared/ui/input';
import { Button } from '@shared/ui/button';
import { useNavigate } from 'react-router-dom';

interface EditRateModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  id: number;
  currentVersion?: string;
}

export const EditRateModal: React.FC<EditRateModalProps> = ({
  isOpen,
  onOpenChange,
  id,
  currentVersion,
}) => {
  const navigate = useNavigate();
  const [editOption, setEditOption] = useState<'new' | 'schedule'>('new');
  const [scheduleDate, setScheduleDate] = useState('');

  const nextVersion = `version-${(parseInt(String(currentVersion || "version-1").replace(/\D/g, ''), 10) || 1) + 1}`;

  const handleContinue = () => {
    navigate(`/plan-tarrif/tariff/edit/${id}`, { state: { scheduleDate, editOption } });
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Editing an active rate</DialogTitle>
          <DialogDescription>
            Editing this tariff creates {nextVersion}. Historical billing remains unaffected.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <RadioGroup
            value={editOption}
            onValueChange={(val: any) => setEditOption(val)}
            className="gap-4"
          >
            {/* Option 1 */}
            <div
              className={`relative flex cursor-pointer rounded-lg border p-4 shadow-sm focus:outline-none ${
                editOption === 'new' ? 'border-blue-600 ring-1 ring-blue-600 bg-blue-50/50' : 'border-gray-300'
              }`}
              onClick={() => setEditOption('new')}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center">
                  <div className="text-sm">
                    <RadioGroupItem value="new" id="new-version" className="sr-only" />
                    <Label
                      htmlFor="new-version"
                      className={`font-medium cursor-pointer ${
                        editOption === 'new' ? 'text-blue-900' : 'text-gray-900'
                      }`}
                    >
                      Create New Version
                    </Label>
                    <p className={`mt-1 cursor-pointer ${editOption === 'new' ? 'text-blue-700' : 'text-gray-500'}`}>
                      Roll all changes into {nextVersion} and apply on save.
                    </p>
                  </div>
                </div>
                <div
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                    editOption === 'new' ? 'border-blue-600' : 'border-gray-300'
                  }`}
                >
                  {editOption === 'new' && (
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                  )}
                </div>
              </div>
            </div>

            {/* Option 2 */}
            <div
              className={`relative flex cursor-pointer flex-col rounded-lg border p-4 shadow-sm focus:outline-none ${
                editOption === 'schedule' ? 'border-blue-600 ring-1 ring-blue-600 bg-blue-50/50' : 'border-gray-300'
              }`}
              onClick={() => setEditOption('schedule')}
            >
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center">
                  <div className="text-sm">
                    <RadioGroupItem value="schedule" id="schedule-version" className="sr-only" />
                    <Label
                      htmlFor="schedule-version"
                      className={`font-medium cursor-pointer ${
                        editOption === 'schedule' ? 'text-blue-900' : 'text-gray-900'
                      }`}
                    >
                      Schedule Future Version
                    </Label>
                    <p className={`mt-1 cursor-pointer ${editOption === 'schedule' ? 'text-blue-700' : 'text-gray-500'}`}>
                      Apply changes on a future date — current version remains active until then.
                    </p>
                  </div>
                </div>
                <div
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                    editOption === 'schedule' ? 'border-blue-600' : 'border-gray-300'
                  }`}
                >
                  {editOption === 'schedule' && (
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />
                  )}
                </div>
              </div>

              {/* Conditional Date Picker */}
              {editOption === 'schedule' && (
                <div className="mt-4 border-t border-blue-200 pt-4" onClick={(e) => e.stopPropagation()}>
                  <Label htmlFor="activate-on" className="text-sm font-medium text-blue-900 mb-1.5 block">
                    Activate on
                  </Label>
                  <Input
                    type="date"
                    id="activate-on"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="bg-white border-blue-200"
                  />
                </div>
              )}
            </div>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleContinue} disabled={editOption === 'schedule' && !scheduleDate}>
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
